import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getUserData } from '../../services/API';
import useIdleTimer from '../Reader/Phrase/hooks/useIdleTimer';
import { resolveIdleTimeouts } from '../Reader/Phrase/hooks/resolveIdleTimeouts';
import useIsMounted from '../Reader/Phrase/hooks/useIsMounted';
import { TriggerActions, triggerDataShallowEqual, POOL_NARRATION_TARGETS, BOARD_TARGETS, MEDIA_TARGETS, isTriggerTarget } from '../../constants/constants';
import { COPY_STRINGS } from '../../constants/translatedCopy';
import { logSessionInfo } from '../../services/logger';
import {
  IDLE_ENCOURAGEMENT_TIMEOUT,
  IDLE_SUBMIT_TIMEOUT,
  MAX_REPLAYS,
  HIGHLIGHT_DURATION,
  TIME_BETWEEN_POOL_ITEMS,
  TIME_BETWEEN_SILENT_POOL_ITEMS,
  parseContextScreen,
} from './formativeScreenConstants';

const EVERYTHING_SHOWN = {
  prompt: false,
  media: false,
  board: false,
  pool: false,
  submit: false,
  repeat: false,
  undo: false,
};

const EVERYTHING_HIDDEN = {
  prompt: true,
  media: true,
  board: true,
  pool: true,
  submit: true,
  repeat: true,
  undo: true,
};

export default function useFormativeScreen({
  name,
  log,
  speech,
  pool,
  hasImage,
  hasVideo,
  readOptionsAloud,
  isInstruction,
  isRepeating,
  shouldExtendTimeout,
  timeoutExtensionMultiplier,
  idleTimeoutOverrides,
  idleEncouragementText,
  instructionRepeatable,
  instructionMaxRepeats,
  instructionRepeatVariation,
  repeatInstructions,
  handleRepeatInstructions,
  beginOverlayRepeatPlayback,
  endOverlayRepeatPlayback,
  setHideParentRepeatButton,
  currentTriggerAction,
  currentTriggerData,
  onTriggerActionComplete,
  deferPoolNarration,
  verbalInstructions,
  onClearBoard,
  onClearSelection,
  encodeResponse,
  onDone,
}) {
  const [hidden, setHidden] = useState(EVERYTHING_SHOWN);
  const [revealedPoolKeys, setRevealedPoolKeys] = useState([]);
  const [playingPoolKey, setPlayingPoolKey] = useState(null);
  const [narrationPassActive, setNarrationPassActive] = useState(false);
  const [stimulusReplaysUsed, setStimulusReplaysUsed] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [canInteract, setCanInteract] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [inFTUE, setInFTUE] = useState(false);
  const [pointerTarget, setPointerTarget] = useState(null);
  const [showRepeatOverlay, setShowRepeatOverlay] = useState(false);
  const [repeatOverlayInputEnabled, setRepeatOverlayInputEnabled] = useState(false);
  const [repeatOverlayPlaybackBusy, setRepeatOverlayPlaybackBusy] = useState(false);

  const isMountedRef = useIsMounted();
  const inFTUERef = useRef(false);
  const submittedRef = useRef(false);
  const submissionDispatchedRef = useRef(false);
  const prevTriggerRef = useRef(null);
  const prevTriggerDataRef = useRef(null);
  const triggerVideoResolverRef = useRef(null);
  const videoPromiseResolverRef = useRef(null);
  const poolNarrationGenerationRef = useRef(0);
  const poolNarrationAwaitPendingRef = useRef(false);
  const pointerTimeoutRef = useRef(null);
  const playInstructionsGenerationRef = useRef(0);
  const repeatOverlayPendingRef = useRef(false);
  const repeatOverlayReplayInFlightRef = useRef(false);
  const overlayRepeatTimersActiveRef = useRef(false);

  const onTriggerActionCompleteRef = useRef(onTriggerActionComplete);
  onTriggerActionCompleteRef.current = onTriggerActionComplete;
  const setHideParentRepeatButtonRef = useRef(setHideParentRepeatButton);
  setHideParentRepeatButtonRef.current = setHideParentRepeatButton;
  const endOverlayRepeatPlaybackRef = useRef(endOverlayRepeatPlayback);
  endOverlayRepeatPlaybackRef.current = endOverlayRepeatPlayback;
  const speechRef = useRef(speech);
  speechRef.current = speech;
  const onClearBoardRef = useRef(onClearBoard);
  onClearBoardRef.current = onClearBoard;
  const onClearSelectionRef = useRef(onClearSelection);
  onClearSelectionRef.current = onClearSelection;
  const encodeResponseRef = useRef(encodeResponse);
  encodeResponseRef.current = encodeResponse;
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const revealedKeys = useMemo(() => new Set(revealedPoolKeys), [revealedPoolKeys]);

  const narratesPool = readOptionsAloud || pool.some((poolItem) => !!poolItem.audioText);

  useEffect(() => {
    submittedRef.current = submitted;
    if (!submitted) submissionDispatchedRef.current = false;
  }, [submitted]);

  const encouragementText = idleEncouragementText || repeatInstructions;

  const {
    idleEncouragementTimeout: extendedIdleEncouragementTimeout,
    idleSubmitTimeout: extendedSubmitTimeout,
  } = resolveIdleTimeouts(
    idleTimeoutOverrides,
    { idleEncouragementTimeout: IDLE_ENCOURAGEMENT_TIMEOUT, idleSubmitTimeout: IDLE_SUBMIT_TIMEOUT },
    shouldExtendTimeout,
    timeoutExtensionMultiplier
  );

  const {
    resetIdleTimer,
    clearIdleTimer,
    idleSpeechPlaying,
    idleTimeoutExpired,
  } = useIdleTimer(speech, extendedIdleEncouragementTimeout, extendedSubmitTimeout);

  const idleEncouragementPlayedRef = useRef(false);

  useEffect(() => {
    if (idleSpeechPlaying) idleEncouragementPlayedRef.current = true;
  }, [idleSpeechPlaying]);

  const armIdleTimer = useCallback(
    (idleEncouragementText) => {
      if (idleEncouragementPlayedRef.current) return;
      resetIdleTimer(idleEncouragementText);
    },
    [resetIdleTimer]
  );

  const holdIdleTimerForNarration = useCallback(() => {
    if (idleEncouragementPlayedRef.current) return;
    clearIdleTimer();
  }, [clearIdleTimer]);

  const interactionLocked = isInstruction || isRepeating;
  const boardDisabled = interactionLocked || !canInteract || submitted || inFTUE;

  const unmountVideoWhenDone = useMemo(() => getUserData().sessionData.isiPad, []);

  const stimulusReplayBudget =
    instructionMaxRepeats == null ? Infinity : Math.max(0, instructionMaxRepeats);
  const stimulusReplaysRemaining = stimulusReplayBudget - stimulusReplaysUsed;
  const hasRepeatToPlay =
    !!handleRepeatInstructions && String(repeatInstructions || '').trim() !== '';
  const consumeStimulusReplay = useCallback(() => setStimulusReplaysUsed((used) => used + 1), []);

  const contextScreen = useMemo(
    () => parseContextScreen(verbalInstructions),
    [verbalInstructions]
  );

  const onContextScreen = !!contextScreen;
  const onContextScreenRef = useRef(onContextScreen);
  onContextScreenRef.current = onContextScreen;
  const canReplayContext = onContextScreen && stimulusReplaysRemaining > 0;

  const repeatVariationTrimmed = String(instructionRepeatVariation || '').trim();
  const suppressVideoReplayOverlay =
    (isInstruction && instructionRepeatable === true && repeatVariationTrimmed !== '') ||
    showRepeatOverlay ||
    repeatOverlayPlaybackBusy;

  const nothingElseOnScreen =
    hidden.prompt && hidden.board && hidden.pool && hidden.submit && hidden.undo && hidden.repeat;
  const stimulusVisible = !hidden.media && (hasImage || hasVideo);
  const layout = {
    imageOnly: onContextScreen && nothingElseOnScreen && stimulusVisible && hasImage && !hasVideo,
    videoOnly: nothingElseOnScreen && stimulusVisible && hasVideo,
    audioOnly: nothingElseOnScreen && !stimulusVisible && showRepeatOverlay,
  };

  const reveal = useCallback((patch) => setHidden((previous) => ({ ...previous, ...patch })), []);

  const speakPoolItem = useCallback(
    (poolItem) =>
      speech.speakWithPromise({
        textToSpeak: poolItem.audioText || `"${poolItem.text || poolItem.key}".`,
        skipTranslation: getUserData().bilingualMode,
        useGenerative: true,
      }),
    [speech]
  );

  const narratePoolItem = useCallback(
    async (poolItem) => {
      if (!narratesPool && !poolItem.audioText) return;
      setPlayingPoolKey(poolItem.key);
      try {
        await speakPoolItem(poolItem);
      } finally {
        setPlayingPoolKey((current) => (current === poolItem.key ? null : current));
      }
    },
    [narratesPool, speakPoolItem]
  );

  const narratePoolItemRef = useRef(narratePoolItem);
  narratePoolItemRef.current = narratePoolItem;

  const narratePoolInOrder = useCallback(
    async (generation, { revealAsWeGo }) => {
      if (!narratesPool && !revealAsWeGo) return;
      const isStale = () =>
        !isMountedRef.current || poolNarrationGenerationRef.current !== generation;

      const gapBetweenTiles = narratesPool
        ? TIME_BETWEEN_POOL_ITEMS
        : TIME_BETWEEN_SILENT_POOL_ITEMS;
      setNarrationPassActive(narratesPool);
      try {
        for (const poolItem of pool) {
          await new Promise((resolve) => {
            setTimeout(() => {
              if (isStale()) {
                resolve();
                return;
              }
              if (revealAsWeGo) {
                setRevealedPoolKeys((previous) => [...previous, poolItem.key]);
              }
              narratePoolItemRef.current(poolItem).finally(resolve);
            }, gapBetweenTiles);
          });
          if (isStale()) break;
        }
      } finally {
        // Generation-guarded like the loop body: a stale pass must not clear the flag
        // out from under the pass that superseded it, or the tiles stop dimming mid-narration.
        if (isMountedRef.current && poolNarrationGenerationRef.current === generation) {
          setNarrationPassActive(false);
        }
      }
    },
    [pool, narratesPool, isMountedRef]
  );

  const revealAndNarratePoolRef = useRef(null);
  revealAndNarratePoolRef.current = (generation) =>
    narratePoolInOrder(generation, { revealAsWeGo: true });
  const narrateRevealedPoolRef = useRef(null);
  narrateRevealedPoolRef.current = (generation) =>
    narratePoolInOrder(generation, { revealAsWeGo: false });

  const revealWholePool = useCallback(() => {
    setRevealedPoolKeys(pool.map((poolItem) => poolItem.key));
  }, [pool]);

  const playVideo = useCallback(async () => {
    const videoPromise = new Promise((resolve) => {
      videoPromiseResolverRef.current = resolve;
    });
    setVideoPlaying(true);
    await videoPromise;
    if (!isMountedRef.current) return;
    setVideoPlaying(false);
    videoPromiseResolverRef.current = null;
  }, [isMountedRef]);

  const showTargets = useCallback(
    (targets) => {
      const ftuePoolInBatch =
        inFTUERef.current && targets.some((target) => isTriggerTarget(POOL_NARRATION_TARGETS, target));
      let pendingSyncComplete = false;
      const markPendingSyncComplete = () => {
        if (!ftuePoolInBatch) pendingSyncComplete = true;
      };

      const overlayBelongsToThisScreen = () =>
        onContextScreenRef.current ||
        (instructionRepeatable === true && repeatVariationTrimmed !== '');

      for (const target of targets) {
        if (isTriggerTarget(BOARD_TARGETS, target)) {
          reveal({ board: false });
          markPendingSyncComplete();
          continue;
        }
        if (isTriggerTarget(POOL_NARRATION_TARGETS, target)) {
          reveal({ pool: false, board: false });
          if (inFTUERef.current && !deferPoolNarration) {
            poolNarrationGenerationRef.current += 1;
            const generation = poolNarrationGenerationRef.current;
            setRevealedPoolKeys([]);
            poolNarrationAwaitPendingRef.current = true;
            (async () => {
              try {
                await revealAndNarratePoolRef.current(generation);
              } finally {
                // A superseded pass owns neither the pending flag nor the awaited
                // completion. Clearing or releasing them from here would hand the live
                // pass's trigger back to Phrase early.
                if (poolNarrationGenerationRef.current === generation) {
                  poolNarrationAwaitPendingRef.current = false;
                  if (isMountedRef.current && !repeatOverlayPendingRef.current) {
                    onTriggerActionCompleteRef.current?.();
                  }
                }
              }
            })();
          } else {
            revealWholePool();
            pendingSyncComplete = true;
          }
          continue;
        }
        if (isTriggerTarget(MEDIA_TARGETS, target)) {
          reveal({ media: false });
          continue;
        }

        switch (target) {
          case 'repeat':
            reveal({ repeat: false });
            break;
          case 'prompt':
            reveal({ prompt: false, repeat: false });
            break;
          case 'undo':
            reveal({ undo: false });
            markPendingSyncComplete();
            break;
          case 'repeatOverlay': {
            if (!overlayBelongsToThisScreen()) {
              markPendingSyncComplete();
            } else {
              setRepeatOverlayInputEnabled(false);
              setShowRepeatOverlay(true);
              markPendingSyncComplete();
            }
            break;
          }
          case 'repeatOverlayInput': {
            if (!overlayBelongsToThisScreen()) {
              markPendingSyncComplete();
            } else {
              setPointerTarget(null);
              setShowRepeatOverlay(true);
              setRepeatOverlayInputEnabled(true);
              repeatOverlayPendingRef.current = true;
            }
            break;
          }
          case 'submit':
            reveal({ submit: false });
            break;
          default:
            log(`show: unknown target "${target}"`);
            break;
        }
      }

      if (pendingSyncComplete && !repeatOverlayPendingRef.current) {
        queueMicrotask(() => onTriggerActionCompleteRef.current?.());
      }
    },
    [
      deferPoolNarration,
      instructionRepeatable,
      repeatVariationTrimmed,
      revealWholePool,
      reveal,
      isMountedRef,
      log,
    ]
  );

  const hideTargets = useCallback(
    (targets) => {
      for (const target of targets) {
        if (isTriggerTarget(BOARD_TARGETS, target)) {
          reveal({ board: true });
          onClearBoardRef.current?.();
          continue;
        }
        if (isTriggerTarget(POOL_NARRATION_TARGETS, target)) {
          poolNarrationGenerationRef.current += 1;
          poolNarrationAwaitPendingRef.current = false;
          reveal({ pool: true });
          setRevealedPoolKeys([]);
          onClearSelectionRef.current?.();
          continue;
        }
        if (isTriggerTarget(MEDIA_TARGETS, target)) {
          reveal({ media: true });
          continue;
        }

        switch (target) {
          case 'repeat':
            reveal({ repeat: true });
            break;
          case 'prompt':
            reveal({ prompt: true, repeat: true });
            break;
          case 'undo':
            reveal({ undo: true });
            break;
          case 'repeatOverlay':
            setShowRepeatOverlay(false);
            setRepeatOverlayInputEnabled(false);
            if (repeatOverlayPendingRef.current) {
              repeatOverlayPendingRef.current = false;
              queueMicrotask(() => onTriggerActionCompleteRef.current?.());
            }
            break;
          case 'submit':
            reveal({ submit: true });
            break;
          default:
            log(`hide: unknown target "${target}"`);
            break;
        }
      }
    },
    [reveal, log]
  );

  const highlightTargets = useCallback((targets) => {
    setPointerTarget(targets[0] || null);
  }, []);

  const clickTargets = useCallback((targets) => {
    const target = targets[0];
    if (!target) {
      queueMicrotask(() => onTriggerActionCompleteRef.current?.());
      return;
    }

    if (target === 'video') {
      setHidden((previous) => ({ ...previous, media: false }));
      setPointerTarget('video');
      setVideoPlaying(true);
      triggerVideoResolverRef.current = () => {
        setVideoPlaying(false);
        setPointerTarget(null);
        triggerVideoResolverRef.current = null;
        onTriggerActionCompleteRef.current?.();
      };
      return;
    }

    setPointerTarget(target);
    pointerTimeoutRef.current = setTimeout(() => {
      pointerTimeoutRef.current = null;
      if (!isMountedRef.current) return;
      setPointerTarget(null);
      onTriggerActionCompleteRef.current?.();
    }, HIGHLIGHT_DURATION);
  }, [isMountedRef]);

  const playTargets = useCallback(
    (targets) => {
      const narratesPoolTarget = targets.some((target) => isTriggerTarget(POOL_NARRATION_TARGETS, target));
      if (!narratesPoolTarget) {
        queueMicrotask(() => onTriggerActionCompleteRef.current?.());
        return;
      }
      poolNarrationGenerationRef.current += 1;
      const generation = poolNarrationGenerationRef.current;
      revealWholePool();
      poolNarrationAwaitPendingRef.current = true;
      (async () => {
        try {
          await narrateRevealedPoolRef.current(generation);
        } finally {
          if (poolNarrationGenerationRef.current === generation) {
            poolNarrationAwaitPendingRef.current = false;
            if (isMountedRef.current) onTriggerActionCompleteRef.current?.();
          }
        }
      })();
    },
    [revealWholePool, isMountedRef]
  );

  const replayContextScreen = useCallback(async () => {
    if (
      !repeatOverlayInputEnabled ||
      repeatOverlayReplayInFlightRef.current ||
      repeatOverlayPlaybackBusy ||
      isRepeating ||
      stimulusReplaysRemaining <= 0
    ) {
      return;
    }
    repeatOverlayReplayInFlightRef.current = true;
    setRepeatOverlayPlaybackBusy(true);
    setShowRepeatOverlay(false);
    consumeStimulusReplay();
    try {
      const narration = contextScreen?.narration;
      if (narration) {
        await speech.speakWithPromise({
          textToSpeak: narration,
          skipTranslation: getUserData().bilingualMode,
          useGenerative: true,
        });
      }
      if (hasVideo && isMountedRef.current) await playVideo();
    } finally {
      if (isMountedRef.current) {
        repeatOverlayReplayInFlightRef.current = false;
        setRepeatOverlayPlaybackBusy(false);
        if (repeatOverlayPendingRef.current) setShowRepeatOverlay(true);
      }
    }
  }, [
    repeatOverlayInputEnabled,
    repeatOverlayPlaybackBusy,
    isRepeating,
    stimulusReplaysRemaining,
    consumeStimulusReplay,
    contextScreen,
    speech,
    hasVideo,
    playVideo,
    isMountedRef,
  ]);

  const handleRepeatOverlayReplay = useCallback(() => {
    if (onContextScreen) {
      replayContextScreen();
      return;
    }
    if (
      !repeatVariationTrimmed ||
      !repeatOverlayInputEnabled ||
      repeatOverlayReplayInFlightRef.current ||
      repeatOverlayPlaybackBusy ||
      isRepeating
    ) {
      return;
    }
    repeatOverlayReplayInFlightRef.current = true;
    setRepeatOverlayPlaybackBusy(true);
    setShowRepeatOverlay(false);

    if (beginOverlayRepeatPlayback) {
      if (!beginOverlayRepeatPlayback()) {
        repeatOverlayReplayInFlightRef.current = false;
        setRepeatOverlayPlaybackBusy(false);
        if (isInstruction && instructionRepeatable === true && repeatOverlayPendingRef.current) {
          setShowRepeatOverlay(true);
        }
        return;
      }
      overlayRepeatTimersActiveRef.current = true;
    }

    const finishReplay = () => {
      if (!isMountedRef.current) return;
      repeatOverlayReplayInFlightRef.current = false;
      setRepeatOverlayPlaybackBusy(false);
      if (overlayRepeatTimersActiveRef.current) {
        overlayRepeatTimersActiveRef.current = false;
        endOverlayRepeatPlayback?.();
      }
      if (isInstruction && instructionRepeatable === true && repeatOverlayPendingRef.current) {
        setShowRepeatOverlay(true);
      }
    };

    speech.speak(
      {
        textToSpeak: repeatVariationTrimmed,
        passThroughSpeechTags: true,
        useGenerative: true,
        onEnd: finishReplay,
        onError: finishReplay,
      },
      false
    );
  }, [
    onContextScreen,
    replayContextScreen,
    repeatVariationTrimmed,
    repeatOverlayInputEnabled,
    repeatOverlayPlaybackBusy,
    isRepeating,
    isInstruction,
    instructionRepeatable,
    beginOverlayRepeatPlayback,
    endOverlayRepeatPlayback,
    speech,
    isMountedRef,
  ]);

  const handleRepeatOverlayNext = useCallback(() => {
    if (
      !repeatOverlayInputEnabled ||
      repeatOverlayReplayInFlightRef.current ||
      isRepeating ||
      repeatOverlayPlaybackBusy
    ) {
      return;
    }
    repeatOverlayPendingRef.current = false;
    setRepeatOverlayInputEnabled(false);
    setShowRepeatOverlay(false);
    onTriggerActionCompleteRef.current?.();
  }, [isRepeating, repeatOverlayPlaybackBusy, repeatOverlayInputEnabled]);

  const handleTrigger = useCallback(
    (action, data) => {
      log(`handleTrigger("${action}", [${data.join(',')}])`);
      if (pointerTimeoutRef.current) {
        clearTimeout(pointerTimeoutRef.current);
        pointerTimeoutRef.current = null;
      }
      setPointerTarget(null);

      switch (action) {
        case TriggerActions.Start:
          clearIdleTimer();
          setHidden(EVERYTHING_HIDDEN);
          setCanInteract(false);
          // Retiring the generation stops a narration pass that outlived the reset from
          // re-populating revealedPoolKeys behind it, the way hideTargets already does.
          poolNarrationGenerationRef.current += 1;
          poolNarrationAwaitPendingRef.current = false;
          setRevealedPoolKeys([]);
          onClearBoardRef.current?.();
          onClearSelectionRef.current?.();
          inFTUERef.current = true;
          setInFTUE(true);
          setShowRepeatOverlay(false);
          setRepeatOverlayInputEnabled(false);
          repeatOverlayPendingRef.current = false;
          break;
        case TriggerActions.Ready:
          inFTUERef.current = false;
          setInFTUE(false);
          setShowRepeatOverlay(false);
          setRepeatOverlayInputEnabled(false);
          repeatOverlayPendingRef.current = false;
          break;
        case TriggerActions.Show:
          showTargets(data);
          break;
        case TriggerActions.Hide:
          hideTargets(data);
          break;
        case TriggerActions.Highlight:
          highlightTargets(data);
          break;
        case TriggerActions.Click:
          clickTargets(data);
          break;
        case TriggerActions.Play:
          playTargets(data);
          break;
        default:
          log(`unknown trigger action "${action}"`);
          break;
      }
    },
    [showTargets, hideTargets, highlightTargets, clickTargets, playTargets, clearIdleTimer, log]
  );

  const handleTriggerRef = useRef(handleTrigger);
  handleTriggerRef.current = handleTrigger;

  useEffect(() => {
    if (!currentTriggerAction) {
      prevTriggerRef.current = null;
      prevTriggerDataRef.current = null;
      return;
    }
    const data = currentTriggerData || [];
    const sameAction = currentTriggerAction === prevTriggerRef.current;
    const sameData = triggerDataShallowEqual(data, prevTriggerDataRef.current);
    // Compared by value because Phrase hands over a fresh array identity every render.
    // This relies on consecutive identical awaitable triggers being unauthorable: a script
    // holding [play:options] twice in a row would have the second one swallowed here and
    // Phrase would wait on a completion that never comes.
    if (sameAction && sameData) return;
    prevTriggerRef.current = currentTriggerAction;
    prevTriggerDataRef.current = [...data];
    handleTriggerRef.current(currentTriggerAction, data);
  }, [currentTriggerAction, currentTriggerData]);

  const startInteraction = useCallback(() => {
    setCanInteract(true);
    reveal({ submit: false });
    armIdleTimer(encouragementText);
  }, [encouragementText, armIdleTimer, reveal]);

  const playInstructions = useCallback(async () => {
    playInstructionsGenerationRef.current += 1;
    const generation = playInstructionsGenerationRef.current;
    setCanInteract(false);
    onClearBoardRef.current?.();
    onClearSelectionRef.current?.();
    clearIdleTimer();

    if (pool.length > 0) {
      if (narratesPool) {
        setRevealedPoolKeys([]);
        poolNarrationGenerationRef.current += 1;
        await revealAndNarratePoolRef.current(poolNarrationGenerationRef.current);
      } else {
        if (!isMountedRef.current || playInstructionsGenerationRef.current !== generation) return;
        revealWholePool();
      }
    }

    if (!isMountedRef.current || playInstructionsGenerationRef.current !== generation) return;

    if (hasVideo) await playVideo();

    if (!isMountedRef.current || playInstructionsGenerationRef.current !== generation) return;
    startInteraction();
  }, [
    pool,
    narratesPool,
    revealWholePool,
    hasVideo,
    playVideo,
    clearIdleTimer,
    startInteraction,
    isMountedRef,
  ]);

  const handleSubmit = useCallback(() => {
    if (submissionDispatchedRef.current) return;
    submissionDispatchedRef.current = true;
    idleEncouragementPlayedRef.current = false;
    clearIdleTimer();
    speech.abortSpeech?.();
    setSubmitted(true);
    setCanInteract(false);
    onClearSelectionRef.current?.();
    if (onDoneRef.current) onDoneRef.current(encodeResponseRef.current());
  }, [clearIdleTimer, speech]);

  const playInstructionsRef = useRef(playInstructions);
  playInstructionsRef.current = playInstructions;
  const startInteractionRef = useRef(startInteraction);
  startInteractionRef.current = startInteraction;
  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;
  const revealWholePoolRef = useRef(revealWholePool);
  revealWholePoolRef.current = revealWholePool;

  useEffect(() => {
    setHideParentRepeatButtonRef.current?.(true);
    return () => {
      setHideParentRepeatButtonRef.current?.(false);
    };
  }, []);

  useEffect(() => {
    return () => {
      let needComplete = false;
      // The pointer rest owns the completion for [click:...], which is awaitable. Clearing
      // the timer without releasing it leaves Phrase waiting on a callback that can no
      // longer fire.
      if (pointerTimeoutRef.current) {
        clearTimeout(pointerTimeoutRef.current);
        pointerTimeoutRef.current = null;
        needComplete = true;
      }
      if (overlayRepeatTimersActiveRef.current) {
        overlayRepeatTimersActiveRef.current = false;
        endOverlayRepeatPlaybackRef.current?.();
      }
      const resolveVideoPromise = videoPromiseResolverRef.current;
      if (resolveVideoPromise) {
        videoPromiseResolverRef.current = null;
        resolveVideoPromise();
      }
      if (triggerVideoResolverRef.current) {
        triggerVideoResolverRef.current = null;
        needComplete = true;
      }
      if (repeatOverlayPendingRef.current) {
        repeatOverlayPendingRef.current = false;
        needComplete = true;
      }
      if (poolNarrationAwaitPendingRef.current) {
        poolNarrationAwaitPendingRef.current = false;
        needComplete = true;
      }
      if (needComplete) onTriggerActionCompleteRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (idleSpeechPlaying === undefined) return;
    setCanInteract(!idleSpeechPlaying);
  }, [idleSpeechPlaying]);

  useEffect(() => {
    if (!idleTimeoutExpired) return;
    if (isInstruction || isRepeating || inFTUE || submitted) return;
    setSubmitted(true);
    setCanInteract(false);

    speechRef.current
      .speakWithPromise({ textToSpeak: COPY_STRINGS.lets_move_on })
      .then(() => {
        if (!isMountedRef.current) return;
        handleSubmitRef.current();
      })
      .catch((error) => {
        logSessionInfo(`${name}_idle_timeout_speech_failed`, { message: error?.message });
        if (!isMountedRef.current) return;
        handleSubmitRef.current();
      });
  }, [idleTimeoutExpired, isInstruction, isRepeating, inFTUE, submitted, name, isMountedRef]);

  useEffect(() => {
    if (isInstruction) {
      submissionDispatchedRef.current = false;
      return;
    }
    setHidden((previous) => ({
      ...previous,
      prompt: false,
      media: false,
      board: false,
      pool: false,
      undo: false,
      repeat: false,
    }));
    if (inFTUERef.current) {
      inFTUERef.current = false;
      setInFTUE(false);
      onClearBoardRef.current?.();
      onClearSelectionRef.current?.();
      revealWholePoolRef.current();
      startInteractionRef.current();
    } else {
      inFTUERef.current = false;
      setInFTUE(false);
      playInstructionsRef.current();
    }
  }, [isInstruction]);

  const onRepeatClick = useCallback(() => {
    if (!interactionLocked && canInteract && hasRepeatToPlay) {
      holdIdleTimerForNarration();
      handleRepeatInstructions();
    }
  }, [interactionLocked, canInteract, hasRepeatToPlay, holdIdleTimerForNarration, handleRepeatInstructions]);

  const onVideoStart = useCallback(() => {
    holdIdleTimerForNarration();
    setCanInteract(false);
  }, [holdIdleTimerForNarration]);

  const onVideoDone = useCallback(() => {
    if (!isMountedRef.current) return;
    if (triggerVideoResolverRef.current) {
      triggerVideoResolverRef.current();
      const resolvePlayInstructions = videoPromiseResolverRef.current;
      if (resolvePlayInstructions) {
        videoPromiseResolverRef.current = null;
        resolvePlayInstructions();
      }
      return;
    }
    const resolveVideo = videoPromiseResolverRef.current;
    if (resolveVideo) {
      videoPromiseResolverRef.current = null;
      resolveVideo();
    }
    if (submittedRef.current) return;
    setCanInteract(true);
    armIdleTimer(encouragementText);
  }, [armIdleTimer, encouragementText, isMountedRef]);

  return {
    hidden,
    layout,
    canInteract,
    submitted,
    inFTUE,
    interactionLocked,
    boardDisabled,
    pointerTarget,
    revealedKeys,
    playingPoolKey,
    narrationPassActive,
    videoPlaying,
    unmountVideoWhenDone,
    maxVideoReplays:
      (submitted || onContextScreen) ? 0 : Math.min(MAX_REPLAYS, stimulusReplaysRemaining),
    consumeStimulusReplay,
    suppressVideoReplayOverlay,
    showRepeatOverlay,
    showRepeatOverlayReplay: onContextScreen ? canReplayContext : true,
    handleRepeatOverlayReplay,
    handleRepeatOverlayNext,
    repeatDisabled: interactionLocked || !canInteract || !hasRepeatToPlay,
    onRepeatClick,
    onVideoStart,
    onVideoDone,
    narratePoolItem,
    encouragementText,
    resetIdleTimer: armIdleTimer,
    clearIdleTimer: holdIdleTimerForNarration,
    handleSubmit,
  };
}
